import React, { useState } from "react";
import { Mail, Phone, MapPin, IdCard, User, Calendar, Briefcase, Heart, MessageSquare, ShieldAlert, UserCheck, AlertTriangle, Sparkles } from "lucide-react";
import PatientRecordAIReviewCard from "./ai/PatientRecordAIReviewCard";
import { useAuth } from "../contexts/AuthContext";

/**
 * Props:
 * - selectedPatient (obj)
 * - formatDate (fn)
 * - formatCurrency (fn) [não usamos aqui, mas deixei compatível]
 * - getTotalTreatmentCost (fn) [não usamos aqui]
 * - getTotalInvoiceAmount (fn) [não usamos aqui]
 * - getTotalPaidAmount (fn) [não usamos aqui]
 */

const FieldRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5">
        <Icon className="w-4 h-4 text-gray-500" />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm text-gray-900">{value}</p>
      </div>
    </div>
  );
};

const calculateAge = (birthDate) => {
  try {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return `${age} anos`;
  } catch {
    return null;
  }
};

const onlyDigits = (value) => String(value || "").replace(/\D+/g, "");

const formatCpfDisplay = (value) => {
  const v = onlyDigits(value).slice(0, 11);
  if (v.length !== 11) return value || null;
  return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}`;
};

const formatPhoneDisplay = (value) => {
  const v = onlyDigits(value).slice(0, 11);
  if (v.length === 10) return `(${v.slice(0, 2)}) ${v.slice(2, 6)}-${v.slice(6)}`;
  if (v.length === 11) return `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
  return value || null;
};

const OverviewTab = ({ selectedPatient, formatDate }) => {
  const { user } = useAuth();
  const canUseAI = (user?.permissions || []).includes("ai.clinical_use");
  const [showAIReview, setShowAIReview] = useState(true);
  if (!selectedPatient) return null;

  const {
    full_name,
    birth_date,
    gender,
    cpf,
    rg,
    email,
    phone,
    address,
    emergency_contact_name,
    emergency_contact_phone,
    is_minor_or_dependent,
    legal_guardian_name,
    legal_guardian_cpf,
    legal_guardian_phone,
    legal_guardian_relationship,
    clinical_alerts,
    administrative_notes,
    profession,
    marital_status,
    reason_for_consultation,

    preferred_name,
    accessibility_needs,
    advance_directives,

    authorized_contact_name,
    authorized_contact_phone,
    authorized_contact_relationship,

    created_at,
  } = selectedPatient;

  const age = calculateAge(birth_date);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {canUseAI && <div className="lg:col-span-3">
        {showAIReview ? (
          <PatientRecordAIReviewCard
            patientId={selectedPatient.id}
            onClose={() => setShowAIReview(false)}
          />
        ) : (
          <div className="flex justify-end">
            <button type="button" onClick={() => setShowAIReview(true)} className="inline-flex items-center gap-2 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-100">
              <Sparkles className="h-4 w-4" /> Resumir prontuário com IA
            </button>
          </div>
        )}
      </div>}

      {/* Cartão: Informações Pessoais */}
      <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações Pessoais</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldRow icon={User} label="Nome" value={full_name} />
          <FieldRow
            icon={User}
            label="Nome social / preferido"
            value={preferred_name}
          />
          <FieldRow icon={IdCard} label="CPF" value={formatCpfDisplay(cpf)} />
          <FieldRow icon={IdCard} label="RG / Documento complementar" value={rg} />
          <FieldRow
            icon={Calendar}
            label="Data de nascimento"
            value={birth_date ? `${formatDate(birth_date)}${age ? ` • ${age}` : ""}` : null}
          />
          <FieldRow icon={User} label="Gênero" value={gender} />
          <FieldRow icon={Mail} label="E-mail" value={email} />
          <FieldRow icon={Phone} label="Telefone" value={formatPhoneDisplay(phone)} />
          <FieldRow icon={UserCheck} label="Contato de emergência" value={emergency_contact_name} />
          <FieldRow icon={Phone} label="Telefone do contato de emergência" value={formatPhoneDisplay(emergency_contact_phone)} />
          <FieldRow
            icon={UserCheck}
            label="Pessoa autorizada a receber informações"
            value={authorized_contact_name}
          />

          <FieldRow
            icon={Phone}
            label="Telefone da pessoa autorizada"
            value={formatPhoneDisplay(authorized_contact_phone)}
          />

          <FieldRow
            icon={User}
            label="Vínculo da pessoa autorizada"
            value={authorized_contact_relationship}
          />
          <FieldRow icon={MapPin} label="Endereço" value={address} />
          <FieldRow icon={Briefcase} label="Profissão" value={profession} />
          <FieldRow icon={Heart} label="Estado civil" value={marital_status} />
          <FieldRow icon={MessageSquare} label="Motivo da consulta" value={reason_for_consultation} />
          <FieldRow
            icon={AlertTriangle}
            label="Necessidades de acessibilidade"
            value={accessibility_needs}
          />

          <FieldRow
            icon={ShieldAlert}
            label="Diretivas antecipadas"
            value={advance_directives}
          />

          {is_minor_or_dependent && (
            <>
              <div className="md:col-span-2 border-t pt-3 mt-2">
                <h4 className="text-sm font-semibold text-gray-700">
                  Responsável legal / representante
                </h4>
              </div>

              <FieldRow icon={UserCheck} label="Responsável legal" value={legal_guardian_name} />
              <FieldRow icon={IdCard} label="CPF do responsável legal" value={formatCpfDisplay(legal_guardian_cpf)} />
              <FieldRow icon={Phone} label="Telefone do responsável legal" value={formatPhoneDisplay(legal_guardian_phone)} />
              <FieldRow icon={User} label="Parentesco / vínculo" value={legal_guardian_relationship} />
            </>
          )}
        </div>

        {created_at && (
          <p className="text-xs text-gray-500 mt-4">
            Cadastrado em {formatDate(created_at)}
          </p>
        )}
      </div>

      {/* Cartão: Resumo rápido (pode crescer no futuro) */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumo</h3>
        <ul className="text-sm text-gray-700 space-y-2">
          <li>
            <span className="text-gray-500">Prontuário de:</span> <span className="font-medium">{full_name}</span>
          </li>
          {age && (
            <li>
              <span className="text-gray-500">Idade:</span> <span className="font-medium">{age}</span>
            </li>
          )}
          {gender && (
            <li>
              <span className="text-gray-500">Gênero:</span> <span className="font-medium">{gender}</span>
            </li>
          )}
          {cpf && (
            <li>
              <span className="text-gray-500">CPF:</span> <span className="font-medium">{formatCpfDisplay(cpf)}</span>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default OverviewTab;
